import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { FCC } from '@eluve/components';
import {
  LoggerArgs,
  LoggingContextProvider,
  useExtendedLogger,
} from '@eluve/logger';
import { usePostHog } from '@eluve/posthog-react';
import {
  useImpersonation,
  useSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { usePersistentStorage } from '@eluve/utility-hooks';

// Mounted above ApolloProvider in router.tsx — do not add useQuery / useApolloClient here.
export const UserContextProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const postHog = usePostHog();
  const { requestPermission } = usePersistentStorage();
  const { isImpersonating } = useImpersonation();
  const session = useSession();

  const { pathname } = useLocation();

  const rolesKey =
    session['https://hasura.io/jwt/claims']['x-hasura-allowed-roles'].join(',');
  const roles = useMemo(() => rolesKey.split(','), [rolesKey]);

  useEffect(
    function setUserContextForServices() {
      postHog.identify(userId, {
        email: session.email,
        firstName: session.firstName || undefined,
        lastName: session.lastName || undefined,
        isEluveAdmin: session.isEluveAdmin,
        isMlEngineer: session.isMlEngineer,
        isEluveEvalUser: session.isEluveEvalUser,
        roles,
        createdAt: session.createdAt,
        displayName: session.displayName || undefined,
        specialty: session.specialty || undefined,
        thirdPartyId: session.thirdPartyId || undefined,
      });

      if (isImpersonating && session.impersonation) {
        postHog.register({
          is_impersonating: true,
          impersonator_id: session.impersonation.impersonatorId,
        });
      } else {
        postHog.unregister('is_impersonating');
        postHog.unregister('impersonator_id');
      }

      Sentry.setUser({
        id: userId,
        email: session.email,
        impersonator_id: session.impersonation?.impersonatorId,
      });

      // Request persistent storage permission on first login
      if (userId) {
        requestPermission();
      }
    },
    [
      userId,
      session.email,
      session.firstName,
      session.lastName,
      session.isEluveAdmin,
      session.isMlEngineer,
      session.isEluveEvalUser,
      session.createdAt,
      session.displayName,
      session.specialty,
      session.thirdPartyId,
      roles,
      postHog,
      requestPermission,
      isImpersonating,
      session.impersonation?.impersonatorId,
      session.impersonation,
    ],
  );

  useEffect(() => {
    postHog.capture('$pageview');
  }, [pathname, postHog]);

  const loggerArgs: LoggerArgs = useMemo(() => {
    return {
      name: 'UserScopedLogger',
      metadata: { userId },
    };
  }, [userId]);

  const userLogger = useExtendedLogger(loggerArgs);

  return (
    <LoggingContextProvider loggerInput={userLogger}>
      {children}
    </LoggingContextProvider>
  );
};
